import { useEffect } from 'react';
import { useIcibAdmin } from '../context/IcibAdminContext';
import { useDrawer } from '../../drawer/DrawerContext';
import LifeRail from '../components/LifeRail';
import { fmtWaiting } from '../utils/format';

/* Overview — landing view of the dashboard once signed in. Pulls live
   data from the backend: the notification queue (GET /notifications
   ?company={gid}, ICIB-03a, scoped to the currently-selected group via its
   documented `group` filter) plus the group's headline counts (ICIB-15,
   GET /companies/{gid}/overview) — server-computed, not derived client-side
   from the ICIB-01a/01b roster the way this view used to. The lifecycle
   key below is static reference content, not live data, so it's shown as-is.

   Each notification row now carries `employee_name` directly (added to
   ICIB-03a's contract), so a row renders immediately without waiting on
   anything else — the drill-down that resolves the rest (email, product,
   group) is findEnrolment/loadEnrolmentDetail below, which only ever
   fetches ICIB-03b (GET /enrolments/{enrolment_id}) for the handful of
   rows actually in the queue (capped at 5). This deliberately does NOT
   also load the group's employee roster/ICIB-01b details (unlike this
   view's own earlier version) — that was an N+1 prefetch of every
   employee in the group just to serve the same handful of rows ICIB-03b
   already resolves on its own. */
function OverviewView() {
  const {
    db, status, groupId, curGroup, openNotifs, findEnrolment, loadEnrolmentDetail, product,
    loadNotifications,
    groupOverview, groupOverviewStatus, loadGroupOverview,
  } = useIcibAdmin();
  const { openDrawer } = useDrawer();

  useEffect(() => { loadNotifications(groupId); }, [groupId, loadNotifications]);
  useEffect(() => { loadGroupOverview(groupId); }, [groupId, loadGroupOverview]);

  const queue = openNotifs().slice(0, 5);
  useEffect(() => {
    queue.forEach((n) => { if (!findEnrolment(n.enrolment_id)) loadEnrolmentDetail(n.enrolment_id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queue.map((n) => n.enrolment_id).join(',')]);

  if (status === 'loading') {
    return <div className="card"><div className="empty"><b>Loading…</b></div></div>;
  }
  if (status === 'error' || !db.me) {
    return <div className="card"><div className="empty"><b>Couldn't load the dashboard</b>Check your connection and try again.</div></div>;
  }

  const overview = groupId ? groupOverview[groupId] : null;
  const overviewLoading = groupId && groupOverviewStatus === 'loading' && !overview;
  const totalEmployees = overview?.total_employees ?? 0;
  const onboarded = overview?.fully_onboarded_employees ?? 0;
  const invited = overview?.employees_with_invited_enrolment ?? 0;
  const removedCount = overview?.employees_with_removed_enrolment ?? 0;

  return (
    <>
      <div className="eyebrow">Overview</div>
      <div className="page-h"><h2>Good morning, {db.me.first_name}</h2></div>
      <p className="page-sub">
        {groupId
          ? <>Both the notification queue below and the counts are for {curGroup()?.name || 'the selected company'} — invitations that have gone quiet, and removals the insurer hasn't confirmed back yet.</>
          : 'Pick a company from the scope selector above to see its register, notification queue, and enrolment counts.'}
      </p>

      {groupId ? (
        <>
          <div className="sec-h">
            <span className="spacer"></span>
            <span className="ep">GET /companies/{'{company_id}'}/overview</span>
          </div>
          <div className="grid g4">
            <div className="card stat"><div className="n">{overviewLoading ? '—' : totalEmployees}</div><div className="l">Total employee in the company</div></div>
            <div className="card stat"><div className="n">{overviewLoading ? '—' : onboarded}</div><div className="l">Employee Enrolled</div></div>
            <div className="card stat warn"><div className="n">{overviewLoading ? '—' : invited}</div><div className="l">Awaiting enrolment</div></div>
            <div className="card stat warn"><div className="n">{overviewLoading ? '—' : removedCount}</div><div className="l">Removals unconfirmed</div></div>
          </div>

          <div className="sec-h">
            <h3>Needs your attention</h3>
            <span className="spacer"></span>
            <span className="ep">GET /notifications?company={'{company}'}</span>
          </div>
          <div className="card">
            {queue.length ? (
              <div className="tbl-wrap">
                <table>
                  <thead><tr><th>Person</th><th>Why it's here</th><th>Waiting</th><th></th></tr></thead>
                  <tbody>
                    {queue.map((n) => {
                      const en = findEnrolment(n.enrolment_id);
                      return (
                        <tr className="clickable" key={n.id} onClick={() => openDrawer(
                          n.employee_name || 'Notification',
                          `ICIB-03b · ${n.id}`,
                          <div>{n.message}</div>
                        )}>
                          <td>
                            <span className="strong">{n.employee_name || 'Unknown'}</span><br />
                            <span className="mono dim">{en ? en.emp.user.email : '—'}</span>
                          </td>
                          <td>
                            {n.type === 'stale_invitation' ? 'Invitation not actioned' : 'Removal not confirmed by insurer'}
                            <br /><span className="mono dim">{en ? product(en.product_id)?.name || en.product_id : '—'}</span>
                          </td>
                          <td className="mono nowrap">{fmtWaiting(n)}</td>
                          <td><button className="btn sm">Open</button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="empty"><b>Nothing outstanding</b>Every invitation has been actioned and every removal confirmed.</div>
            )}
          </div>
        </>
      ) : (
        <div className="card"><div className="empty"><b>Select a company</b>Choose a company from the scope selector above.</div></div>
      )}

      <div className="sec-h"><h3>How a person moves through the register</h3></div>
      <div className="card card-b">
        <div className="life-key">
          <div>
            <LifeRail status="invited" withLabel />
            <b style={{ marginTop: 6 }}>Invitation sent</b>
            Flagged automatically after 10 days of silence.
          </div>
          <div>
            <LifeRail status="onboarded" withLabel />
            <b style={{ marginTop: 6 }}>Cover in force</b>
            Confirmed against the insurer.
          </div>
          <div>
            <LifeRail status="removed" withLabel />
            <b style={{ marginTop: 6 }}>Removal recorded</b>
            Waiting on the insurer to confirm.
          </div>
          <div>
            <LifeRail status="archived" withLabel />
            <b style={{ marginTop: 6 }}>Closed</b>
            Insurer has confirmed the removal.
          </div>
        </div>
        <div className="note" style={{ marginTop: 14 }}>
          <b>Where an application fails</b>
          An insurer can decline an application. That enrolment stops at{' '}
          <LifeRail status="onboarding_unsuccessful" withLabel /> and never reaches cover.
        </div>
      </div>
    </>
  );
}

export default OverviewView;
